import * as React from "react"
import { cn } from "@/lib/utils"
import { X } from "lucide-react"
import { Button } from "./button"

export interface NativeDialogProps extends React.HTMLAttributes<HTMLDialogElement> {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
  showCloseButton?: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl'
}

export interface NativeDialogTriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  children: React.ReactNode
}

export interface NativeDialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export interface NativeDialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export interface NativeDialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export interface NativeDialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode
}

export interface NativeDialogDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode
}

const NativeDialogContext = React.createContext<{
  dialogRef: React.RefObject<HTMLDialogElement>
  open: boolean
  setOpen: (open: boolean) => void
}>({
  dialogRef: { current: null },
  open: false,
  setOpen: () => {},
})

const NativeDialog = ({ 
  children, 
  open, 
  onOpenChange,
  showCloseButton = true,
  size = '4xl', // Default size
  ...props 
}: NativeDialogProps) => {
  const dialogRef = React.useRef<HTMLDialogElement>(null)
  const [isOpen, setIsOpen] = React.useState(open || false)

  const sizeMap = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    '2xl': "max-w-2xl",
    '3xl': "max-w-3xl",
    '4xl': "max-w-4xl",
    '5xl': "max-w-5xl",
  };

  React.useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open)
    }
  }, [open])

  React.useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleClose = () => {
      setIsOpen(false)
      onOpenChange?.(false)
    }

    dialog.addEventListener('close', handleClose)
    return () => {
      dialog.removeEventListener('close', handleClose)
    }
  }, [onOpenChange])

  React.useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (isOpen && !dialog.open) {
      dialog.showModal()
    } else if (!isOpen && dialog.open) {
      dialog.close()
    }
  }, [isOpen])

  const setOpen = React.useCallback((open: boolean) => {
    setIsOpen(open)
    onOpenChange?.(open)
  }, [onOpenChange])

  return (
    <NativeDialogContext.Provider value={{ dialogRef, open: isOpen, setOpen }}>
      <dialog
        ref={dialogRef}
        className={cn(
          "fixed z-50 bg-background p-6 shadow-lg backdrop:bg-black backdrop:opacity-30 rounded-lg border",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[2%] data-[state=open]:slide-in-from-top-[2%]",
          sizeMap[size],
          "w-full mx-auto"
        )}
        {...props}
      >
        {showCloseButton && (
          <Button
            onClick={() => dialogRef.current?.close()}
            className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            variant="ghost"
            size="sm"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        )}
        {children}
      </dialog>
    </NativeDialogContext.Provider>
  )
}

const NativeDialogTrigger = ({ 
  children, 
  asChild,
  ...props 
}: NativeDialogTriggerProps) => {
  const { setOpen } = React.useContext(NativeDialogContext)

  if (asChild) {
    return React.cloneElement(children as React.ReactElement, {
      ...props,
      onClick: (e: React.MouseEvent) => {
        setOpen(true)
        ;(children as React.ReactElement).props.onClick?.(e)
      },
    })
  }

  return (
    <Button {...props} onClick={() => setOpen(true)}>
      {children}
    </Button>
  )
}

const NativeDialogContent = ({ 
  children,
  className,
  ...props 
}: NativeDialogContentProps) => {
  return (
    <div
      className={cn("relative", className)}
      {...props}
    >
      {children}
    </div>
  )
}

const NativeDialogHeader = ({
  className,
  ...props
}: NativeDialogHeaderProps) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left mb-4", className)}
    {...props}
  />
)

const NativeDialogFooter = ({
  className,
  ...props
}: NativeDialogFooterProps) => (
  <div
    className={cn("mt-4 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
)

const NativeDialogTitle = ({
  className,
  ...props
}: NativeDialogTitleProps) => (
  <h2
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
)

const NativeDialogDescription = ({
  className,
  ...props
}: NativeDialogDescriptionProps) => (
  <p
    className={cn("text-sm text-muted-foreground max-w-[80%]", className)}
    {...props}
  />
)

export {
  NativeDialog,
  NativeDialogTrigger,
  NativeDialogContent,
  NativeDialogHeader,
  NativeDialogFooter,
  NativeDialogTitle,
  NativeDialogDescription,
}
